import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../../hooks/common/useAuth.ts';
import type { ReactNode } from 'react';
import { MOCK_MODE } from '@qcdoc/mock-runtime/adapter.ts';
import { db } from '@qcdoc/mock-runtime/helpers.ts';
import { AUTH_PORTAL, LOGIN_PATH, IS_INDEPENDENT_PORTAL } from '../../app/portal.ts';
import { AUTH_MODE } from '../../services/auth/config.ts';

interface AuthGuardProps {
  children: ReactNode;
  /** Sign-in route to send anonymous users to (vendor portal uses its own). */
  redirectTo?: string;
}

/**
 * Route guard that requires authentication.
 * Redirects unauthenticated users to the sign-in page.
 */
export default function AuthGuard({ children, redirectTo = '/login' }: AuthGuardProps) {
  const { isAuthenticated, isLoading, user, roles } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated && AUTH_MODE !== 'local' && AUTH_MODE !== 'mock-sso' && AUTH_MODE !== 'dev') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={IS_INDEPENDENT_PORTAL ? LOGIN_PATH : redirectTo} state={{ from: location }} replace />;
  }

  if (IS_INDEPENDENT_PORTAL && user?.portal !== AUTH_PORTAL) {
    return <Navigate to="/access-denied" replace />;
  }

  if (MOCK_MODE) {
    const rule = db().accessPages.find((page) => page.pageUrl === location.pathname && page.isOverride);
    if (rule && !roles.some((role) => ['Admin', 'SuperAdmin', 'QCDocAdmin', ...(rule.roles as string[])].includes(role))) return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}
